import { Component, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { AccountService } from '../../../../../../services/account/account.service';
import { DocumentTemplateService } from '../../../../../../services/document-template/document-template.service';
import {
  DocumentTemplate
} from '../../../../../../models/document-template';
import { OrganizationConfigService } from '../../../../../../services/organization-config/organization-config.service';
import { ComponentHasUnsavedChanges } from '../../../../../../unsaved-changes-guard.service';
import { SimpleMessageModalComponent } from '../../../../../../components/modals/simple-message-modal/simple-message-modal.component';

enum EditModes {
  settings,
  sections,
  linkedProducts
}

@Component({
  selector: 'app-document-template-edit',
  templateUrl: './document-template-edit.component.html',
  styleUrls: ['./document-template-edit.component.css']
})
export class DocumentTemplateEditComponent implements OnInit, ComponentHasUnsavedChanges {

  constructor(
    public orgConfigService: OrganizationConfigService,
    private route: ActivatedRoute,
    private router: Router,
    private accountService: AccountService,
    private documentTemplateService: DocumentTemplateService,
    private modalService: NgbModal
  ) {}

  public documentTemplate: DocumentTemplate;

  public documentTemplateSuccessMessage = '';
  public documentTemplateErrorMessage = '';
  public documentTemplateErrors: any = {};

  public loadingTemplate = false;

  public templateNotFound = false;
  public serverError: number = null;
  public unsavedChanges = false;

  public EditModes = EditModes;
  public currentEditMode = this.EditModes.sections;

  ngOnInit() {
    const routeParams = this.route.snapshot.params;

    this.loadDocumentTemplate(routeParams.id);
  }

  hasUnsavedChanges() {
    return this.unsavedChanges;
  }

  @HostListener('window:beforeunload')
  allowUnload() {
    return !this.hasUnsavedChanges();
  }

  public loadDocumentTemplate(templateId: string) {
    this.loadingTemplate = true;

    const templateIdInt = parseInt(templateId, 10);
    if (isNaN(templateIdInt)) {
      this.templateNotFound = true;
      this.loadingTemplate = false;
      return;
    }

    this.documentTemplateService
      .getDocumentTemplate(templateIdInt)
      .then(template => {
        this.documentTemplate = template;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.templateNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingTemplate = false; });
  }

  public changeEditMode(newEditMode: EditModes) {
    if (!this.unsavedChanges) {
      this.loadDocumentTemplate(this.documentTemplate.id.toString());
    }
    this.currentEditMode = newEditMode;
  }

  /**
   * Save the document template.
   *
   * If an optional edit mode is provided, switch to the given edit mode if the
   * save is successful.
   */
  public handleSave() {
    this.loadingTemplate = true;

    this.documentTemplateSuccessMessage = '';
    this.documentTemplateErrorMessage = '';
    this.documentTemplateErrors = {};

    this.documentTemplateService
      .updateDocumentTemplate(this.documentTemplate)
      .then(updatedDocumentTemplate => {
        this.documentTemplate = updatedDocumentTemplate;

        this.documentTemplateSuccessMessage = '\'' + this.documentTemplate.name + '\' saved successfully.';

        this.unsavedChanges = false;

        // Clear message after five seconds (if it was not cleared earlier).
        setTimeout(() => {
          if (this.documentTemplateSuccessMessage !== '') {
            this.documentTemplateSuccessMessage = '';
          }
        }, 5000);
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.documentTemplateErrors = reason.error;
          this.documentTemplateErrorMessage = 'Please correct the provided errors and try again.';
        } else if (reason.status === 403) {
          this.documentTemplateErrorMessage = 'You do not have permission to modify this document template.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingTemplate = false; });
  }

  /**
   * Prompt for confirmation, then retire the document template (PRO-863).
   *
   * Linked products are fetched first so the confirmation modal can warn that
   * the template will stay linked to them.
   */
  public handleRetire() {
    this.documentTemplateService
      .getLinkedProducts(this.documentTemplate.id)
      .then(linkedProducts => {
        this.openRetireConfirmationModal(linkedProducts.event_types.length);
      })
      .catch(() => {
        // If the linked products can't be loaded, still allow retiring.
        this.openRetireConfirmationModal(0);
      });
  }

  private openRetireConfirmationModal(linkedProductCount: number) {
    const modalRef = this.modalService.open(SimpleMessageModalComponent);
    modalRef.componentInstance.headerText = 'Retire Document Template';
    modalRef.componentInstance.messageText =
      'Are you sure you want to retire \'' + this.documentTemplate.name + '\'? ' +
      'It will be hidden from the document templates list, from the templates ' +
      'available when signing documents, and from document-related settings ' +
      'such as event participant agreements. Existing documents created from ' +
      'this template will not be affected, and you can restore the template ' +
      'later if needed.';
    modalRef.componentInstance.messageAlertClass = 'alert-danger';

    if (linkedProductCount > 0) {
      const isSingular = linkedProductCount === 1;
      modalRef.componentInstance.secondaryMessageText =
        'This template is linked to ' + linkedProductCount + ' ' +
        (isSingular ? 'product' : 'products') + '. Retiring it will not remove ' +
        (isSingular ? 'this link' : 'these links') + '; the template will ' +
        'remain linked to ' + (isSingular ? 'it' : 'them') + '. See the ' +
        '"Linked Products" tab for the list.';
      modalRef.componentInstance.secondaryMessageAlertClass = 'alert-info';
    }

    modalRef.componentInstance.hasConfirmation = true;
    modalRef.componentInstance.confirmedCallback = () => {
      this.performRetire();
    };
  }

  /**
   * Prompt for confirmation, then restore the retired document template
   * (PRO-863).
   */
  public handleRestore() {
    const modalRef = this.modalService.open(SimpleMessageModalComponent);
    modalRef.componentInstance.headerText = 'Restore Document Template';
    modalRef.componentInstance.messageText =
      'Are you sure you want to restore \'' + this.documentTemplate.name + '\'? ' +
      'It will become active again and reappear in the document templates list ' +
      'and wherever templates are available.';
    modalRef.componentInstance.messageAlertClass = 'alert-warning';
    modalRef.componentInstance.hasConfirmation = true;
    modalRef.componentInstance.confirmedCallback = () => {
      this.performRestore();
    };
  }

  private performRetire() {
    this.loadingTemplate = true;
    this.documentTemplateSuccessMessage = '';
    this.documentTemplateErrorMessage = '';

    this.documentTemplateService
      .retireDocumentTemplate(this.documentTemplate.id)
      .then(updatedDocumentTemplate => {
        this.documentTemplate = updatedDocumentTemplate;
        this.documentTemplateSuccessMessage =
          '\'' + this.documentTemplate.name + '\' has been retired.';
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.documentTemplateErrorMessage = 'You do not have permission to retire this document template.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingTemplate = false; });
  }

  private performRestore() {
    this.loadingTemplate = true;
    this.documentTemplateSuccessMessage = '';
    this.documentTemplateErrorMessage = '';

    this.documentTemplateService
      .restoreDocumentTemplate(this.documentTemplate.id)
      .then(updatedDocumentTemplate => {
        this.documentTemplate = updatedDocumentTemplate;
        this.documentTemplateSuccessMessage =
          '\'' + this.documentTemplate.name + '\' has been restored.';
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.documentTemplateErrorMessage = 'You do not have permission to restore this document template.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingTemplate = false; });
  }

}
